import { useMemo, useState, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import {
  Table,
  LoadingSkeleton,
  Button,
  useBreakpoints,
} from "@narmi/design_system";
import Filters from "byzantine/src/filters";
import {
  useArchiveTemplate,
  useTemplateActivePaymentsCounts,
} from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates";
import type { FormattedBulkTemplate } from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates/types";
import type { TemplateActivePaymentsCounts } from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates";
import type Pagination from "byzantine/src/Pagination";
import type { SortDirection } from "./useBulkTemplatesContainer";
import PageNavigation from "../../page_navigation/PageNavigation";
import { StatusTag } from "../../PaymentActivity/common";
import { PAYMENT_ACTIVITY_ROUTE } from "../../PaymentActivity/routes";
import { TEMPLATES_ROUTE } from "../constants";
import TemplateActionsMenu from "./TemplateActionsMenu";
import ArchiveTemplateDialog from "./ArchiveTemplateDialog";
import CopyToNewTemplateDialog from "./CopyToNewTemplateDialog";
import EditActiveTemplateDialog from "./EditActiveTemplateDialog";
import BulkTemplateDrawer from "./Drawer/BulkTemplateDrawer";
import styles from "./BulkTemplates.module.scss";

const EMPTY_COUNTS: TemplateActivePaymentsCounts = {
  scheduledBulkCount: 0,
  scheduledIndividualCount: 0,
  pendingBulkCount: 0,
  pendingIndividualCount: 0,
};

type BulkTemplatesTableProps = {
  templates: FormattedBulkTemplate[];
  loading: boolean;
  isFiltered: boolean;
  pagination: Pagination;
  sortDirection: SortDirection;
  onToggleSort: () => void;
  refetchCurrentPage: () => void;
};

const BulkTemplatesTable = ({
  templates,
  loading,
  isFiltered,
  pagination,
  sortDirection,
  onToggleSort,
  refetchCurrentPage,
}: BulkTemplatesTableProps) => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { s } = useBreakpoints();
  const [archivingTemplate, setArchivingTemplate] = useState<{
    template: FormattedBulkTemplate;
    activePaymentsCount: number;
  } | null>(null);
  const { archiveTemplate } = useArchiveTemplate();

  const [copyingTemplateUuid, setCopyingTemplateUuid] =
    useState<API.BulkTemplateId | null>(null);

  const [editingTemplate, setEditingTemplate] = useState<{
    template: FormattedBulkTemplate;
    counts: TemplateActivePaymentsCounts;
    versionId?: string;
  } | null>(null);
  const { fetchActivePaymentsCounts, loading: editLoading } =
    useTemplateActivePaymentsCounts();

  const navigateToEdit = useCallback(
    (templateUuid: string, versionId?: string) => {
      const searchParams = new URLSearchParams({ templateId: templateUuid });
      if (versionId) {
        searchParams.set("versionId", versionId);
      }
      navigate(`/${TEMPLATES_ROUTE}/ach/edit?${searchParams.toString()}`);
    },
    [navigate]
  );

  const handleClickEdit = useCallback(
    (template: FormattedBulkTemplate, versionId?: string) => {
      if (template.state === "active" || template.state === "active_pending") {
        fetchActivePaymentsCounts(template.uuid, {
          onData: (counts) => {
            const hasPayments =
              counts.scheduledBulkCount > 0 || counts.pendingBulkCount > 0;
            if (hasPayments) {
              setEditingTemplate({ template, counts, versionId });
            } else {
              navigateToEdit(template.uuid, versionId);
            }
          },
        });
      } else {
        navigateToEdit(template.uuid, versionId);
      }
    },
    [fetchActivePaymentsCounts, navigateToEdit]
  );

  const handleClickArchive = useCallback(
    (template: FormattedBulkTemplate) => {
      fetchActivePaymentsCounts(template.uuid, {
        onData: (counts) => {
          const activePaymentsCount =
            counts.scheduledBulkCount + counts.pendingBulkCount;
          if (activePaymentsCount === 0) {
            // if no associated payments, archive directly without dialog
            archiveTemplate(template.uuid, false, {
              onSuccess: () => {
                refetchCurrentPage();
              },
            });
          } else {
            // show confirmation dialog when there are associated payments
            setArchivingTemplate({
              template,
              activePaymentsCount,
            });
          }
        },
      });
    },
    [archiveTemplate, fetchActivePaymentsCounts, refetchCurrentPage]
  );

  const selectedId =
    (params.templateUuid as API.BulkTemplateId | undefined) ?? null;
  const selectedIndex = useMemo(
    () => templates.findIndex((t) => t.uuid === selectedId),
    [templates, selectedId]
  );
  const selectedTemplate = templates[selectedIndex] ?? null;
  const isDrawerOpen = Boolean(selectedId);
  const basePath = `${TEMPLATES_ROUTE}/ach`;

  const handleOpenDrawer = (uuid: string) => {
    navigate(`/${basePath}/${uuid}${location.search}`);
  };

  const handleCloseDrawer = () => {
    navigate(`/${basePath}${location.search}`);
  };

  const handlePrev = () => {
    if (selectedIndex <= 0) {
      handleCloseDrawer();
    } else {
      navigate(
        `/${basePath}/${templates[selectedIndex - 1].uuid}${location.search}`
      );
    }
  };

  const handleNext = () => {
    if (selectedIndex === -1 || selectedIndex >= templates.length - 1) {
      handleCloseDrawer();
    } else {
      navigate(
        `/${basePath}/${templates[selectedIndex + 1].uuid}${location.search}`
      );
    }
  };

  return (
    <LoadingSkeleton isLoading={loading}>
      {templates.length === 0 && isFiltered && (
        <div className="margin--top--xl margin--bottom--l">
          <span className="fontSize--l fontWeight--bold">
            {l10n.getString("payment-activity-search-empty")}
          </span>
          <div>{l10n.getString("payment-activity-search-empty-subtitle")}</div>
        </div>
      )}
      {templates.length === 0 && !isFiltered && (
        <p className="fontColor--secondary">
          {l10n.getString("message-no-ach-templates")}
        </p>
      )}
      {templates.length > 0 && (
        <>
          <Table
            colVisibility={["*", "*", "m", "m", "*"]}
            colLayout={{
              s: "2fr 1fr 1fr", // Name, Status, Actions
              m: "2fr 1fr 1fr 1fr 100px", // Name, Status, Last Updated, Updated By, Actions
              l: "2fr 1fr 1fr 1fr 100px", // Name, Status, Last Updated, Updated By, Actions
            }}
          >
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>
                  {l10n.getString("bulk-template-name")}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {l10n.getString("payment-activity-label-status")}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  <Button kind="plain" onClick={onToggleSort}>
                    <span className={styles.sortableHeader}>
                      {l10n.getString("table-header-last-updated")}
                      {sortDirection === "asc" && (
                        <span className="narmi-icon-chevron-up" />
                      )}
                      {sortDirection === "desc" && (
                        <span className="narmi-icon-chevron-down" />
                      )}
                    </span>
                  </Button>
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {l10n.getString("table-header-updated-by")}
                </Table.HeaderCell>
                <Table.HeaderCell textAlign={"right" as any}>
                  {l10n.getString("label-action")}
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {templates.map((template) => (
                <Table.Row
                  key={template.uuid}
                  onRowClick={() => handleOpenDrawer(template.uuid)}
                >
                  <Table.Cell>{template.name}</Table.Cell>
                  <Table.Cell>
                    <StatusTag status={template.displayStatus} isBadge={!s} />
                  </Table.Cell>
                  <Table.Cell>
                    {template.updatedAt
                      ? Filters.longMonthDayYear(template.updatedAt)
                      : ""}
                  </Table.Cell>
                  <Table.Cell>{template.updatedBy ?? ""}</Table.Cell>
                  <Table.Cell>
                    <div
                      className={styles.actionsCell}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <TemplateActionsMenu
                        templateState={template.state}
                        hasSendPayments={template.paymentCreditCount > 0}
                        hasCollectPayments={template.paymentDebitCount > 0}
                        editLoading={editLoading}
                        onEdit={() => handleClickEdit(template)}
                        onMakePayment={() => {
                          navigate(
                            `/${PAYMENT_ACTIVITY_ROUTE}/bulk/new?templateId=${template.uuid}`
                          );
                        }}
                        onDuplicate={() => {
                          setCopyingTemplateUuid(template.uuid);
                        }}
                        onArchive={() => handleClickArchive(template)}
                      />
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          <PageNavigation
            pagination={pagination}
            marginClasses="margin--all--l"
          />
        </>
      )}
      <CopyToNewTemplateDialog
        isOpen={copyingTemplateUuid !== null}
        templateUuid={copyingTemplateUuid}
        onClose={() => setCopyingTemplateUuid(null)}
        onCopy={(versionId) => {
          navigate(
            `/${TEMPLATES_ROUTE}/ach/new?versionId=${versionId}&templateId=${copyingTemplateUuid}`
          );
          setCopyingTemplateUuid(null);
        }}
      />
      <ArchiveTemplateDialog
        isOpen={archivingTemplate !== null}
        templateName={archivingTemplate?.template.name ?? ""}
        pendingPaymentsCount={archivingTemplate?.activePaymentsCount ?? 0}
        onClose={() => setArchivingTemplate(null)}
        onArchive={(cancelActivePayments) => {
          if (archivingTemplate) {
            archiveTemplate(
              archivingTemplate.template.uuid,
              cancelActivePayments,
              {
                onSuccess: () => {
                  setArchivingTemplate(null);
                  refetchCurrentPage();
                },
              }
            );
          }
        }}
      />
      <EditActiveTemplateDialog
        isOpen={editingTemplate !== null}
        counts={editingTemplate?.counts ?? EMPTY_COUNTS}
        onClose={() => setEditingTemplate(null)}
        onKeepEditing={() => {
          if (editingTemplate) {
            navigateToEdit(
              editingTemplate.template.uuid,
              editingTemplate.versionId
            );
            setEditingTemplate(null);
          }
        }}
      />
      {isDrawerOpen && selectedId ? (
        <BulkTemplateDrawer
          isOpen={isDrawerOpen}
          template={selectedTemplate}
          templateUuid={selectedId}
          handleClose={handleCloseDrawer}
          handleOnNext={handleNext}
          handleOnPrev={handlePrev}
          onArchive={handleClickArchive}
          onEdit={handleClickEdit}
          editLoading={editLoading}
        />
      ) : null}
    </LoadingSkeleton>
  );
};

export default BulkTemplatesTable;
